import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TrendingUp, Check, Mail, User, Shield, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    account_type: 'standard',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name) {
      toast.error('Please enter your full name');
      return;
    }
    
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!formData.phone) {
      toast.error('Please enter your phone number');
      return;
    }

    if (!formData.address) {
      toast.error('Please enter your address');
      return;
    }

    setLoading(true);

    try {
      await register(formData);
      toast.success('Account created! Check your email for login credentials.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    '$100,000 Demo Account',
    'Real-time Market Data',
    'Professional Trading Tools',
    'Secure Master & Investor Passwords',
  ];

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white font-['Outfit']">GeneiProFx</span>
            </Link>
            <h1 className="text-3xl font-bold text-white font-['Outfit']">Create Account</h1>
            <p className="mt-2 text-zinc-400">
              Start trading CFDs with $100,000 demo funds
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-zinc-300">First Name</Label>
                <Input
                  id="first_name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary"
                  placeholder="John"
                  data-testid="first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-zinc-300">Last Name</Label>
                <Input
                  id="last_name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary"
                  placeholder="Doe"
                  data-testid="last-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email Address</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary"
                placeholder="john@example.com"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-zinc-300">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary"
                placeholder="+1 234 567 8900"
                data-testid="phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="text-zinc-300">Address</Label>
              <Input
                id="address"
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-primary"
                placeholder="123 Main St, City, Country"
                data-testid="address-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Account Type</Label>
              <Select
                value={formData.account_type}
                onValueChange={(value) => setFormData({ ...formData, account_type: value })}
              >
                <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white">
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="standard" className="text-white hover:bg-zinc-800">
                    Standard Account
                  </SelectItem>
                  <SelectItem value="ecn" className="text-white hover:bg-zinc-800">
                    ECN Account
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">
                {formData.account_type === 'standard' 
                  ? 'Standard: Fixed spreads, no commission' 
                  : 'ECN: Raw spreads + commission'}
              </p>
            </div>

            {/* Password Info Box */}
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-blue-400 font-medium text-sm">Secure Credentials</p>
                  <p className="text-zinc-400 text-xs mt-1">
                    Your <strong>Master Password</strong> (for trading) and <strong>Investor Password</strong> (read-only) 
                    will be automatically generated and sent to your email.
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full glow-primary py-6 text-base"
              data-testid="register-btn"
            >
              {loading ? (
                <>
                  <span className="spinner mr-2" />
                  Creating Account...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5 mr-2" />
                  Create Account & Get Credentials
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-zinc-400">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Benefits */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient opacity-50" />
        <div className="absolute inset-0 grid-pattern" />
        <div className="relative z-10 flex flex-col justify-center px-16">
          <h2 className="text-4xl font-bold text-white mb-8 font-['Outfit']">
            Start Trading in Minutes
          </h2>
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-lg text-zinc-300">{benefit}</span>
              </li>
            ))}
          </ul>
          <div className="mt-12 p-6 rounded-xl bg-zinc-900/50 border border-zinc-800">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-white font-semibold">7-Digit Account ID</p>
                <p className="text-zinc-400 text-sm">
                  Get your unique trading account number instantly
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
